import { maxBy, minBy } from 'lodash';
const rowTypes = {
  SINGLE_IN_GROUP: 'single-in-group',
  GROUPED: 'grouped',
  GROUP_HEADER: 'group-header'
};
const createPickupData = (pickup, rowType) => ({
  ...pickup,
  id: pickup.pickupId,
  groupId: pickup.groupId.toUpperCase(),
  pickupNumberIdList: [
    {
      pickupId: pickup.pickupId,
      pickupNumber: pickup.pickupNumber,
      isPrimary: pickup.isPrimary
    }
  ],
  pickupIdList: [pickup.pickupId],
  pickupNumberList: [pickup.pickupNumber],
  rowType: rowType
});

// Define boolean fields once at the top of the function or module
const BOOLEAN_FIELDS = [
  'isSwapPickup',
  'isDailyPickup',
  'is3PlPickup',
  'isDockDropPickup',
  'isExhibitPickup',
  'isApt',
  'isFood',
  'isPoison',
  'isHazmat',
  'isFreezable',
  'isResidential',
  'isLiftGate',
  'isLimitedAccess',
  'isExcessiveLength',
  'isResunaDeliveryStop',
  'isCodShipment',
  'isLiftgateReqd',
  'isTwoManRequired',
  'isCargoVanEligible',
  'maxEquipmentSize',
  'minEquipmentSize',
  'fixedEquipmentSize',
  'isVoided',
  'isCompleted',
  'isMissed',
  'isAcknowledged'
];

// Optimized aggregation function - only essential fields
const aggregateGroupValues = (groupPickups) => {
  const addToSet = (set, value) => {
    if (Array.isArray(value)) {
      value.forEach(item => set.add(item));
    } else if (value != null) {
      set.add(value);
    }
  };
  const isNoFreight = () => {
    return groupPickups.every((pickup) => pickup.isNoFreight === true);
  };

  const concatWithNewline = (existing, newValue) =>
    existing ? `${existing}\n${newValue}` : newValue;

  // Initialize accumulator with boolean fields
  const initialAcc = {
    bills: 0,
    pieces: 0,
    handlingUnits: 0,
    weight: 0,
    packageTypes: new Set(),
    quoteNumbers: new Set(),
    pickupTypes: new Set(),
    priority: new Set(),
    specialInstructions: '',
    shipperComments: '',
    isNoFreight: false,
    createdDateTime: null,
    pse: 0,
    ...Object.fromEntries(BOOLEAN_FIELDS.map(field => [field, false]))
  };

  const aggregated = groupPickups.reduce(
    (acc, pickup) => {
      // Sum numeric values
      acc.bills += pickup.bills || 0;
      acc.pieces += pickup.pieces || 0;
      acc.handlingUnits += pickup.hu || 0;
      acc.weight += pickup.weight || 0;
      acc.pse += pickup.pse || 0;

      // Add to sets
      addToSet(acc.packageTypes, pickup.packageType);
      addToSet(acc.pickupTypes, pickup.pickupType);
      addToSet(acc.priority, pickup.priority);

      if (pickup.quoteNumber) acc.quoteNumbers.add(pickup.quoteNumber);

      // Concatenate strings
      if (pickup.specialInstructions) {
        acc.specialInstructions = concatWithNewline(
          acc.specialInstructions,
          pickup.specialInstructions
        );
      }
      if (pickup.shipperComments) {
        acc.shipperComments = concatWithNewline(acc.shipperComments, pickup.shipperComments);
      }
      if (
        !acc.createdDateTime ||
        new Date(pickup.createdDateTime) < new Date(acc.createdDateTime)
      ) {
        acc.createdDateTime = pickup.createdDateTime;
      }

      // Boolean OR operations - using loop instead of repetitive assignments
      BOOLEAN_FIELDS.forEach(field => {
        acc[field] ||= !!pickup[field];
      });

      return acc;
    },
    initialAcc
  );

  // Build return object dynamically
  return {
    bills: aggregated.bills,
    pieces: aggregated.pieces,
    handlingUnits: aggregated.handlingUnits,
    weight: aggregated.weight,
    packageTypes: Array.from(aggregated.packageTypes),
    quoteNumbers: Array.from(aggregated.quoteNumbers),
    pickupTypes: Array.from(aggregated.pickupTypes),
    priority: Array.from(aggregated.priority).length ? Array.from(aggregated.priority) : null,
    specialInstructions: aggregated.specialInstructions || null,
    shipperComments: aggregated.shipperComments || null,
    ...Object.fromEntries(BOOLEAN_FIELDS.map(field => [field, aggregated[field]])),
    isNoFreight: isNoFreight(),
    pse: aggregated.pse,
    createdDateTime: aggregated.createdDateTime
  };
};

// Create group header
const createGroupHeader = (groupId, groupPickups, primaryPickup, aggregatedValues) => ({
  ...primaryPickup,
  id: groupId,
  groupId: groupId.toUpperCase(),
  rowType: rowTypes.GROUP_HEADER,
  pickupId: groupPickups.map((p) => p.pickupId).join(', '),
  pickupNumber: '',
  pickupNumberIdList: groupPickups.map((p) => {
    return {
      pickupId: p.pickupId,
      pickupNumber: p.pickupNumber,
      isPrimary: p.isPrimary
    };
  }),
  pickupIdList: groupPickups.map((p) => p.pickupId),
  pickupNumberList: groupPickups.map((p) => p.pickupNumber),
  bills: aggregatedValues.bills,
  pieces: aggregatedValues.pieces,
  hu: aggregatedValues.handlingUnits,
  weight: aggregatedValues.weight,
  packageType: aggregatedValues.packageTypes,
  priority: aggregatedValues.priority,
  allQuoteNumbers: aggregatedValues.quoteNumbers.join(', '),
  specialInstructions: aggregatedValues.specialInstructions,
  allPickupTypes: aggregatedValues.pickupTypes,
  groupCount: groupPickups.length,
  shipperComments: aggregatedValues.shipperComments,
  ...Object.fromEntries(BOOLEAN_FIELDS.filter(field => 
    !['isVoided', 'isCompleted', 'isMissed', 'isAcknowledged'].includes(field)
  ).map(field => [field, aggregatedValues[field]])),
  pse: aggregatedValues.pse,
  pickupCloseTime: maxBy(groupPickups, 'pickupCloseTime')?.pickupCloseTime ?? null,
  pickupReadyTime: minBy(groupPickups, 'pickupReadyTime')?.pickupReadyTime ?? null,
  groupAggregatedData: {
    isVoided: aggregatedValues.isVoided,
    isNoFreight: aggregatedValues.isNoFreight,
    isCompleted: aggregatedValues.isCompleted,
    isMissed: aggregatedValues.isMissed,
    isAcknowledged: aggregatedValues.isAcknowledged,
    pickupDate: primaryPickup.pickupDate,
    driverId: primaryPickup.driverId,
    createdDateTime: aggregatedValues.createdDateTime
  }
});

// Process a single group
const processGroup = (groupId, groupPickups) => {
  if (groupPickups.length === 1) {
    return [createPickupData(groupPickups[0], rowTypes.SINGLE_IN_GROUP)];
  }

  const primaryPickup = groupPickups.find((p) => p.isPrimary) || groupPickups[0];
  const aggregatedValues = aggregateGroupValues(groupPickups);

  const groupHeader = createGroupHeader(groupId, groupPickups, primaryPickup, aggregatedValues);

  const groupedPickups = groupPickups.map((pickup) => createPickupData(pickup, rowTypes.GROUPED));

  return [groupHeader, ...groupedPickups];
};

const createGroupedData = (pickupsList) => {
  if (!pickupsList?.length) return [];

  // Filter pickups that need processing
  const processPickups = pickupsList.filter((pickup) => !pickup.rowType || !isGroupHeaders(pickup));

  // Group pickups by groupId using reduce
  const groupedByGroupId = processPickups.reduce((groups, pickup) => {
    const groupId = pickup.groupId;
    groups[groupId.toUpperCase()] = groups[groupId.toUpperCase()] || [];
    groups[groupId.toUpperCase()].push(pickup);
    return groups;
  }, {});

  const groupItems = Object.entries(groupedByGroupId)
    .map(([groupId, groupPickups]) => processGroup(groupId, groupPickups))
    .flat();

  return groupItems;
};

// Optimized search function for quick filter text
const searchInData = (data, searchText) => {
  if (!data || !searchText) return false;

  const lowerSearchText = searchText.toLowerCase();

  const searchRecursive = (obj) => {
    if (obj === null || obj === undefined) return false;

    // Handle primitives
    if (typeof obj === 'string' || typeof obj === 'number') {
      return obj.toString().toLowerCase().includes(lowerSearchText);
    }

    // Handle arrays - search with early exit using some()
    if (Array.isArray(obj)) {
      return obj.some((item) => searchRecursive(item));
    }

    // Handle objects - search values with early exit using some()
    if (typeof obj === 'object') {
      return Object.values(obj).some((value) => searchRecursive(value));
    }

    return false;
  };

  return searchRecursive(data);
};

const getPickupsInGroup = (groupId, allPickupsList) => {  
  return allPickupsList.filter((pickup) => pickup.groupId.toUpperCase() === groupId.toUpperCase() && !isGroupHeaders(pickup));
};

const isGroupHeaders = (row) => row.rowType === rowTypes.GROUP_HEADER;
const isGroupedPickups = (row) => row.rowType === rowTypes.GROUPED;
const isSingleInGroupPickups = (row) => row.rowType === rowTypes.SINGLE_IN_GROUP;
const isPickupGroupHeader = (row) => isGroupHeaders(row) || isSingleInGroupPickups(row);
const getPrimaryPickupNumberIdList = (pickupNumberIdList) => {
  return pickupNumberIdList.find((pickup) => pickup.isPrimary) || pickupNumberIdList[0];
};

export {
  createGroupedData,
  searchInData,
  isGroupHeaders,
  isGroupedPickups,
  isSingleInGroupPickups,
  isPickupGroupHeader,
  createGroupHeader,
  createPickupData,
  aggregateGroupValues,
  getPickupsInGroup,
  getPrimaryPickupNumberIdList
};
